import { Button, Col, Icon, Input, Row, Tappable, Text, useConfirm, useTheme } from "@rally/ui-kit";
import { useCallback, useEffect, useState } from "react";
import { say } from "../localization/say.ts";
import { act, read } from "../net/rally.ts";

type Secret = { name: string; hint: string; environment: string | null };

export function AppSecrets({
  app,
  environment,
  onChanged,
}: {
  app: string;
  environment: string | null;
  onChanged: () => void;
}) {
  const theme = useTheme();
  const [held, setHeld] = useState<Secret[]>([]);
  const [name, setName] = useState("");
  const [value, setValue] = useState("");
  const [busy, setBusy] = useState(false);
  const [refused, setRefused] = useState<string | null>(null);
  const { ask, dialog } = useConfirm();
  const load = useCallback(async () => {
    const said = await read("app.secrets", { app }).catch(() => ({ secrets: [] }));
    setHeld((said as { secrets: Secret[] }).secrets);
  }, [app]);

  useEffect(() => {
    void load();
  }, [load]);

  const mine = held.filter((one) => one.environment === environment);
  const again = () => {
    void load();
    onChanged();
  };

  const keep = () => {
    setBusy(true);
    setRefused(null);
    void act("app.keepSecret", {
      app,
      name: name.trim(),
      value,
      ...(environment ? { environment } : {}),
    })
      .then(() => {
        setBusy(false);
        setName("");
        setValue("");
        again();
      })
      .catch((cause: unknown) => {
        setBusy(false);
        setRefused(cause instanceof Error ? cause.message : say("app.saveFailed"));
      });
  };

  const forget = async (which: string) => {
    const sure = await ask({
      title: say("app.forgetTitle")(which),
      description: say("app.forgetMeans"),
      confirmLabel: say("app.forgetSecret"),
      cancelLabel: say("app.cancel"),
      tone: "danger",
    });
    if (!sure) return;
    setRefused(null);
    try {
      await act("app.forgetSecret", { app, name: which, ...(environment ? { environment } : {}) });
      again();
    } catch (cause) {
      setRefused(cause instanceof Error ? cause.message : say("app.saveFailed"));
    }
  };

  return (
    <Col gap={2} testID={`app-secrets-${environment ?? "shared"}`}>
      {dialog}
      {mine.map((one) => (
        <Row key={one.name} gap={2} align="center">
          <Icon name="key" size={14} color="text.tertiary" />
          <Text typeName="caption" color="text.secondary" style={{ flex: 1 }}>
            {one.name}
          </Text>
          <Text typeName="caption" color="text.tertiary">
            {one.hint}
          </Text>
          <Tappable
            accessibilityRole="button"
            accessibilityLabel={`${say("app.forgetSecret")} ${one.name}`}
            testID={`forget-secret-${one.name}`}
            onPress={() => void forget(one.name)}
            style={{
              width: 36,
              height: 36,
              alignItems: "center",
              justifyContent: "center",
              borderRadius: theme.radius.md,
            }}
          >
            <Icon name="close-circle" size={16} color="text.tertiary" />
          </Tappable>
        </Row>
      ))}
      <Row gap={2} align="center">
        <Input
          value={name}
          onChangeText={setName}
          placeholder={say("app.secretName")}
          testID={`secret-name-${environment ?? "shared"}`}
          style={{ flex: 1 }}
        />
        <Input
          value={value}
          onChangeText={setValue}
          placeholder={say("app.secretValue")}
          secureTextEntry
          testID={`secret-value-${environment ?? "shared"}`}
          style={{ flex: 1 }}
        />
        <Button
          size="sm"
          onPress={keep}
          disabled={busy || name.trim() === "" || value === ""}
          testID={`keep-secret-${environment ?? "shared"}`}
        >
          {say("app.keepSecret")}
        </Button>
      </Row>
      {refused ? (
        <Text typeName="caption" color="danger">
          {refused}
        </Text>
      ) : null}
    </Col>
  );
}
